import {
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import JiraTimeTextField from 'Components/Common/JiraTimeTextField';
import SelectBrands from 'Components/Common/SelectBrands';
import SelectMultiplePlacements from 'Components/Common/SelectMultiplePlacements';
import useAppSelector from 'Hooks/Redux/useAppSelector';
import useLoadingDispatch from 'Hooks/Redux/useLoadingDispatch';
import useMessagesDispatch from 'Hooks/Redux/useMessagesDispatch';
import BrandGroup from 'Models/Brands/BrandGroup';
import Video from 'Models/Videos/Video';
import VideoDetectionPlacement from 'Models/Videos/VideoDetectionPlacement';
import { ChangeEvent, ReactElement, useState } from 'react';
import postCreateAndExportDetectionsRequest, {
	ExportDetectionVariant,
	PostCreateAndExportDefaultDetectionsRequest,
} from 'Utils/Http/Requests/Videos/Detections/Export/PostCreateAndExportDetectionsRequest';

interface ReportExportWizardProps {
	isOpen: boolean;
	onClose: () => void;
	placements: VideoDetectionPlacement[];
	brandGroups: BrandGroup[];
	selectedVideoIds: number[];
	videos: Video[];
}

enum ExportType {
	DEFAULT,
	TAGS,
	WITHOUT_PLACEMENT,
}

const ReportExportWizard = (props: ReportExportWizardProps): ReactElement => {
	const [exportType, setexportType] = useState<ExportType>(ExportType.DEFAULT);
	const [selectedBrandIds, setSelectedBrandIds] = useState<number[]>([]);
	const [selectedPlacementIds, setSelectedPlacementIds] = useState<number[]>([]);
	const [excelType, setexcelType] = useState(ExportDetectionVariant.Default);
	const [combineExcels, setCombineExcels] = useState(true);
	const [startTime, setStartTime] = useState<number | null>(null);
	const [endTime, setEndTime] = useState<number | null>(null);
	const [separateText, setSeparateText] = useState(false);
	const [shouldUseAverageDetectionSizePerFrame, setShouldShouldUseAverageDetectionSizePerFrame] = useState(false);
	const [includeSummaryLinePerBrandInSwissReport, setIncludeSummaryLinePerBrandInSwissReport] = useState(false);

	const token = useAppSelector<string | null>((state) => state.profile.token);
	const { dispatchStartLoading, dispatchStopLoading } = useLoadingDispatch();
	const { dispatchSetAxiosErrorMessage } = useMessagesDispatch();

	const handleExportTagSwitchChanged = (): void => {
		alert('Not yet implemented!');
	};

	const handleExportWithoutPlacementSwitchChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		setexportType(target.checked ? ExportType.WITHOUT_PLACEMENT : ExportType.DEFAULT);
	};

	const handleCombineExcelCheckboxClicked = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		setCombineExcels(target.checked);
	};

	const handleSeparateTextDetectionCheckboxClicked = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		setSeparateText(target.checked);
	};

	const handleUseAverageDetectionSizePerFrameCheckboxClicked = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		setShouldShouldUseAverageDetectionSizePerFrame(target.checked);
	};

	const handleIncludeSummaryLinePerBrandInSwissReportCheckboxClicked = ({
		target,
	}: ChangeEvent<HTMLInputElement>): void => {
		setIncludeSummaryLinePerBrandInSwissReport(target.checked);
	};

	const handleBrandsSelected = (value: number | number[]): void => {
		if (Array.isArray(value)) {
			setSelectedBrandIds(value);
		}
	};

	const handlePlacementsSelected = setSelectedPlacementIds;

	const handleExcelTypeSelectClicked = ({ target }: ChangeEvent<{ value: unknown }>): void => {
		if (
			target.value === ExportDetectionVariant.Visua ||
			target.value === ExportDetectionVariant.Default ||
			target.value === ExportDetectionVariant.Swiss
		)
			setexcelType(target.value);
	};

	const downloadExcel: (videoIds: number[]) => Promise<void> = async (videoIds) => {
		const request: PostCreateAndExportDefaultDetectionsRequest = {
			brandGroupIds: selectedBrandIds,
			placementIds: selectedPlacementIds,
			videoIds: videoIds,
			withoutPlacement: exportType === ExportType.WITHOUT_PLACEMENT,
			separateText: separateText,
			startTime: startTime,
			endTime: endTime,
			useAverageDetectionSizePerFrame: shouldUseAverageDetectionSizePerFrame,
			combineReports: combineExcels,
			variant: excelType,
			includeSummaryLinePerBrandInSwissReport: includeSummaryLinePerBrandInSwissReport,
		};
		await postCreateAndExportDetectionsRequest(token, request);
	};

	const handleStartTimeChanged = (value: number | null) => {
		setStartTime(value);
	};

	const handleEndTimeChanged = (value: number | null) => {
		setEndTime(value);
	};

	const handleDownloadClick: () => void = async () => {
		dispatchStartLoading('Exporting detections');
		try {
			await downloadExcel(props.selectedVideoIds);
		} catch (e) {
			dispatchSetAxiosErrorMessage(e);
		} finally {
			dispatchStopLoading();
		}
	};

	const renderSelections = (): JSX.Element => {
		if (exportType === ExportType.TAGS) {
			return <>Not Implemented!</>;
		}
		return (
			<>
				<SelectBrands
					brands={props.brandGroups}
					id="detection-wizard-brands"
					onChange={handleBrandsSelected}
					value={selectedBrandIds}
					multiple={true}
				/>
				<SelectMultiplePlacements
					id="detection-wizard-placements"
					selectedPlacementIds={selectedPlacementIds}
					onChange={handlePlacementsSelected}
					placements={props.placements}
				/>
			</>
		);
	};

	return (
		<Dialog open={props.isOpen} onClose={props.onClose} fullWidth={true}>
			<DialogTitle>Export detection report</DialogTitle>
			<DialogContent>
				<FormControl fullWidth={true} margin="normal">
					<FormControlLabel
						control={
							<Checkbox
								checked={exportType === ExportType.TAGS}
								onChange={handleExportTagSwitchChanged}
								name="checkedExportTags"
								color="primary"
							/>
						}
						label="Export with tags"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={exportType === ExportType.WITHOUT_PLACEMENT}
								onChange={handleExportWithoutPlacementSwitchChanged}
								name="checkedWithoutPlacement"
								color="primary"
							/>
						}
						label="Export without placement"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={combineExcels}
								onChange={handleCombineExcelCheckboxClicked}
								name="combineExcelsCheckbox"
								color="primary"
							/>
						}
						label="Combine reports into single excel"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={separateText}
								onChange={handleSeparateTextDetectionCheckboxClicked}
								name="separateTextDetections"
								color="primary"
							/>
						}
						label="Separate text detections"
					/>
					{excelType === ExportDetectionVariant.Default && (
						<FormControlLabel
							control={
								<Checkbox
									checked={shouldUseAverageDetectionSizePerFrame}
									onChange={handleUseAverageDetectionSizePerFrameCheckboxClicked}
									name="sumAverageDetectionSizePerFrame"
									color="primary"
								/>
							}
							label="Sum average detection size per frame"
						/>
					)}
					{excelType === ExportDetectionVariant.Swiss && (
						<FormControlLabel
							control={
								<Checkbox
									checked={includeSummaryLinePerBrandInSwissReport}
									onChange={handleIncludeSummaryLinePerBrandInSwissReportCheckboxClicked}
									name="includeSummaryLinePerBrandInSwissReport"
									color="primary"
								/>
							}
							label="Include summary line per brand in Swiss report"
						/>
					)}
				</FormControl>
				<FormControl fullWidth={true} margin="normal">
					<InputLabel id="export-type">Export type</InputLabel>
					<Select labelId="export-type" id="export-type" value={excelType} onChange={handleExcelTypeSelectClicked}>
						<MenuItem value={ExportDetectionVariant.Default}>Default</MenuItem>
						<MenuItem value={ExportDetectionVariant.Visua}>Visua</MenuItem>
						<MenuItem value={ExportDetectionVariant.Swiss}>Swiss</MenuItem>
					</Select>
				</FormControl>
				{renderSelections()}
				<FormControl fullWidth={true} margin="normal">
					<JiraTimeTextField label="Time from" onChange={handleStartTimeChanged} fullWidth={true} />
				</FormControl>
				<FormControl fullWidth={true}>
					<JiraTimeTextField label={'Time to'} onChange={handleEndTimeChanged} fullWidth={true} />
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button type="button" color="primary" variant="contained" onClick={handleDownloadClick}>
					Download
				</Button>
				<Button type="button" variant="text" onClick={props.onClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ReportExportWizard;
